import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  SitrepSectionType,
  useSitrepV2,
  useUpdateSitrepSectionV2,
  useRemoveSitrepSectionV2,
  useRefreshSitrepV2,
  useCloneSitrepV2,
} from '../DisasterGPT/hooks/useSitrepsV2';
import PageTitle from '../headers/PageTitle';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import dayjs from 'dayjs';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import { useToggle } from 'react-use';
import { EditReportSidepanel } from './EditReportSidepanel';
import MDEditor from '@uiw/react-md-editor';
import { Components } from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { AcknowledgmentStatusAlerts } from '../DisasterGPTAlerts/components/AcknowledgmentStatusAlerts';
import Dropdown from 'react-bootstrap/Dropdown';
import AIContentReportDialog from '../DisasterGPT/AIContentReportDialog';
import { SitrepHistoryPanel } from './SitrepHistoryPanel';

// Pulled from SitrepSection.js
const markdownComponents: Components = {
  table: ({ node: _, ...props }) => (
    <table className="custom-markdown-table" {...props} />
  ),
  th: ({ node: _, ...props }) => (
    <th className="custom-markdown-th" {...props} />
  ),
  td: ({ node: _, ...props }) => (
    <td className="custom-markdown-td" {...props} />
  ),
  tr: ({ node: _, ...props }) => (
    <tr className="custom-markdown-tr" {...props} />
  ),
  a: ({ node: _, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
};

type SitrepSectionProps = {
  section: SitrepSectionType;
  sitrepId: UUID;
  setHistoryPanel: (value: any) => void;
};
const SitrepSection = ({
  section,
  sitrepId,
  setHistoryPanel,
}: SitrepSectionProps) => {
  const [isEditing, setIsEditing] = useToggle(false);
  const { mutate } = useUpdateSitrepSectionV2();
  const removeSitrepSectionMutation = useRemoveSitrepSectionV2();
  const refreshSitrepMutation = useRefreshSitrepV2();
  const [refreshing, setRefreshing] = useState(false);
  const [reported, setReported] = useState<SitrepSectionType | undefined>(
    undefined
  );
  const date =
    section.section_versions[0]?.timestamp;
  const versionNumber = section.versionNumber || 0;
  const sectionText =
    section.section_versions[versionNumber]?.sitrep_section_text ?? undefined;
  const [localSectionText, setLocalSectionText] = useState<string | undefined>(
    sectionText
  );

  const handleEditButtonClick = () => {
    if (
      isEditing &&
      localSectionText !==
        section.section_versions[versionNumber]?.sitrep_section_text
    ) {
      // Save changes
      mutate({
        sitrepId,
        sectionId: section.id,
        updatedFields: { sectionText: localSectionText ?? '' },
      });
    }
    setIsEditing();
  };

  const handleDelete = (sectionId: UUID) => {
    removeSitrepSectionMutation.mutate({
      sitrepId: sitrepId,
      sectionId: sectionId,
    });
  };

  const refreshSection = () => {
    setRefreshing(true);
    refreshSitrepMutation.mutate(
      {
        id: sitrepId,
        selectedDatetime: date,
        sections: [section.id],
      },
      {
        onError: () => {
          setRefreshing(false);
        },
        onSuccess: () => {
          setRefreshing(false);
        },
      }
    );
  };

  return (
    <div className="sitrep-section">
      <div className="sitrep-section-header d-flex flex-row justify-content-between">
        <p className="sitrep-section-header-text align-self-center">
          {`Reported Event: ${section.name} | ${dayjs(section.section_versions[versionNumber]?.timestamp)}`}
        </p>
        <div className="d-flex">
          <StylishNewButton
            className="button--link"
            aria-label="Edit section content"
            title="Edit section content"
            onClick={handleEditButtonClick}
          >
            <SharedIcon iconName="edit" color="light" />
          </StylishNewButton>
          <StylishNewButton
            className="button--link"
            aria-label="Report section content"
            title="Report section content"
            onClick={() => setReported(section)}
          >
            <SharedIcon iconName="error" color="light" />
          </StylishNewButton>
          <Dropdown>
            <Dropdown.Toggle as={StylishNewButton} className="button--link">
              <SharedIcon iconName="history" color="light" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                as="button"
                onClick={() => setHistoryPanel(section)}
              >
                History
              </Dropdown.Item>
              <Dropdown.Item as="button" onClick={() => refreshSection()}>
                Update
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle as={StylishNewButton} className="button--link">
              <SharedIcon iconName="settings" color="light" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                as="button"
                onClick={() => handleDelete(section.id)}
              >
                Delete Section
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div className="sitrep-section-content">
        {refreshing && <div>Refreshing...</div>}
        {isEditing ? (
          <MDEditor
            value={localSectionText}
            onChange={(value) => setLocalSectionText(value)}
            height="100%"
          />
        ) : (
          sectionText && (
            <MDEditor.Markdown
              source={sectionText}
              components={markdownComponents}
              rehypePlugins={[[rehypeSanitize]]}
            />
          )
        )}
      </div>
      <AIContentReportDialog
        reportedContent={reported}
        onClose={() => setReported(undefined)}
      />
    </div>
  );
};

const SingleSitrep = () => {
  const { sitrepId } = useParams();
  const navigate = useNavigate();
  const { data: sitrep, isLoading, isError, isSuccess } = useSitrepV2(sitrepId);
  const [isEditingSitrepSections, setIsEditingSitrepSections] =
    useToggle(false);
  const [historyPanel, setHistoryPanel] = useState<
    SitrepSectionType | undefined
  >(undefined);
  const cloneSitrepMutation = useCloneSitrepV2();

  // Alert acknowledgements logic
  const isAlertSitrep = sitrep?.sitrep_source === 'ALERT';
  const alertEventInstanceId = sitrep?.sitrep_source_id;

  // Prepare sections data (must be before hooks)
  const sortedSitrepSections = useMemo(() => {
    if (!sitrep?.sections) return [];
    return [...sitrep.sections].sort((a, b) => a.order - b.order);
  }, [sitrep]);

  const handleCloneSitrep = () => {
    if (sitrepId) {
      cloneSitrepMutation.mutate(
        { sitrepId },
        {
          onSuccess: (data) => {
            // Navigate to the newly cloned sitrep
            navigate(`/sitreps/${data.sitrepId}`);
          },
        }
      );
    }
  };

  if (isLoading) {
    return <>Loading...</>;
  }

  if (isError || !isSuccess) {
    return <>Error loading Sitrep</>;
  }

  return (
    <div className="site-main">
      <div className="d-flex flex-row w-100">
        <div className="container-fluid p-3">
          <div className="adding-padding">
            <PageTitle title={sitrep.name} parents={['Sitreps']} />

            {/* Alert Acknowledgment Status */}
            <AcknowledgmentStatusAlerts
              isAlertSitrep={isAlertSitrep}
              eventInstanceId={alertEventInstanceId}
              showSummary={true}
            />

            <div className="d-flex gap-3">
              <StylishNewButton
                className="button--primary"
                onClick={setIsEditingSitrepSections}
              >
                Edit Report
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                onClick={handleCloneSitrep}
                disabled={cloneSitrepMutation.isPending}
              >
                {cloneSitrepMutation.isPending ? 'Cloning...' : 'Clone'}
              </StylishNewButton>
            </div>

            <div className="d-flex flex-column gap-3 mt-3">
              {Array.isArray(sortedSitrepSections) &&
                sortedSitrepSections.length > 0 &&
                sortedSitrepSections.map((section) => (
                  <SitrepSection
                    key={section.id}
                    section={section}
                    sitrepId={sitrep.id}
                    setHistoryPanel={setHistoryPanel}
                  />
                ))}
            </div>
          </div>
        </div>
        {isEditingSitrepSections && (
          <EditReportSidepanel
            onHide={() => setIsEditingSitrepSections(false)}
            sortedSitrepSections={sortedSitrepSections}
            sitrepId={sitrep.id}
          />
        )}
        {historyPanel && (
          <SitrepHistoryPanel
            onHide={() => setHistoryPanel(undefined)}
            versions={historyPanel}
            markdownComponents={markdownComponents}
          />
        )}
      </div>
    </div>
  );
};

export default SingleSitrep;
